import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faEdit, faEnvelope, faFilePdf, faFileExcel, faFilter, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import Select from "react-select";
import { canEdit } from "../utils/permissions";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    fontSize: "13px",
    textAlign: "left",
    borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(13,110,253,.15)" : "none",
    alignItems: "center",
  }),
  valueContainer: (base) => ({
    ...base,
    textAlign: "left",
    padding: "2px 8px",
    minHeight: 34,
    flexWrap: "wrap",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
  }),
  singleValue: (base) => ({
    ...base,
    textAlign: "left",
  }),
  multiValue: (base) => ({
    ...base,
    fontSize: "12px",
    margin: "1px 2px",
  }),
  input: (base) => ({
    ...base,
    textAlign: "left",
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    textAlign: "left",
    color: "#6c757d",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    textAlign: "left",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base) => ({
    ...base,
    textAlign: "left",
    direction: "ltr",
    fontSize: "13px",
  }),
};

const DEFAULT_SELECTED_STATUSES = [
  { value: "NEW", label: "NEW" },
  { value: "RE-NEW", label: "RE-NEW" },
];

const BASE_STATUS_OPTIONS = ["NEW", "RE-NEW", "LEFT", "STOPPED"];

const statusBadgeClass = (status) => {
  switch (status) {
    case "NEW":
      return "badge bg-success";
    case "RE-NEW":
      return "badge bg-primary";
    case "LEFT":
      return "badge bg-secondary";
    case "STOPPED":
      return "badge bg-danger";
    default:
      return "badge bg-light text-dark";
  }
};

const calcAmountDays = (amount, duration) => {
  const amt = Number(amount);
  const dur = Number(duration);
  if (!amt || !dur || Number.isNaN(amt) || Number.isNaN(dur)) return null;
  return (amt / 365) * dur;
};

const formatAmountDays = (amount, duration) => {
  const val = calcAmountDays(amount, duration);
  if (val === null) return "-";
  return val.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const EXCEL_BORDER = {
  top: { style: "thin", color: { rgb: "D1D5DB" } },
  bottom: { style: "thin", color: { rgb: "D1D5DB" } },
  left: { style: "thin", color: { rgb: "D1D5DB" } },
  right: { style: "thin", color: { rgb: "D1D5DB" } },
};

const applyReminderExcelStyles = (ws) => {
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const numericCols = new Set([5, 6, 7, 8, 9, 13]);
  const wrapCols = new Set([2, 4, 10, 14]);
  const lastRow = range.e.r;

  for (let r = range.s.r; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };

      if (r === 0) {
        ws[ref].s = {
          fill: { fgColor: { rgb: "0D6EFD" } },
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: EXCEL_BORDER,
        };
      } else if (r === lastRow) {
        ws[ref].s = {
          fill: { fgColor: { rgb: "DCE7FF" } },
          font: { bold: true, color: { rgb: "1E3A8A" }, sz: 11 },
          alignment: {
            horizontal: numericCols.has(c) || c === 7 ? "right" : "left",
            vertical: "center",
          },
          border: EXCEL_BORDER,
        };
        if (numericCols.has(c) && typeof ws[ref].v === "number") {
          ws[ref].z = "#,##0.00";
        }
      } else {
        ws[ref].s = {
          fill: { fgColor: { rgb: r % 2 === 0 ? "FFFFFF" : "F8FAFC" } },
          font: { sz: 10, color: { rgb: "111827" } },
          alignment: {
            horizontal: numericCols.has(c) ? "right" : "left",
            vertical: "center",
            wrapText: wrapCols.has(c),
          },
          border: EXCEL_BORDER,
        };
        if (numericCols.has(c) && typeof ws[ref].v === "number") {
          ws[ref].z = "#,##0.00";
        }
      }
    }
  }

  ws["!cols"] = [
    { wch: 12 },
    { wch: 14 },
    { wch: 28 },
    { wch: 10 },
    { wch: 22 },
    { wch: 10 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
    { wch: 24 },
    { wch: 12 },
    { wch: 12 },
    { wch: 8 },
    { wch: 30 },
  ];
  ws["!rows"] = [{ hpt: 28 }];
  ws["!views"] = [{ state: "frozen", ySplit: 1, activeCell: "A2", showGridLines: true }];
};

export default function Jaantilist() {
  const { http, user } = AuthUser();
  const allowEdit = canEdit(user);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loginUser, setloginUser] = useState(user.department_id);
  const [selectedDepartmentId, setselectedDepartmentId] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_SELECTED_STATUSES);
  const [daysFilter, setDaysFilter] = useState("");
  const [editUser, setEditUser] = useState({});
  const [departments, setdepartments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEditCompany, setSelectedEditCompany] = useState(null);
  const [startDate, setstartDate] = useState("");
  const [endDate, setendDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState({});
  const [reminderTypes, setReminderTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    http.get(`/unique_reminder_company?&login_user=${user.id}`).then((res) => {
      setdepartments(res.data.data);
    });
    http.get("/get_customer_dropdown").then((res) => {
      setCustomers(res.data.customers || []);
    });
    http.get('/reminder_types').then((res) => {
      if (res.data.success) {
        setReminderTypes(res.data.data);
      }
    });
    fetchTaskDetails("", "", "", "", "all");
  }, []);

  const fetchTaskDetails = (
    companyVal = "",
    selstatus = "",
    startDateVal = "",
    endDateVal = "",
    statusFilter = "active"
  ) => {
    setIsLoading(true);
    const endpoint = `/reminder?status=${selstatus}&company=${companyVal}&startdate=${startDateVal}&enddate=${endDateVal}&status_filter=${statusFilter}`;
    http
      .get(endpoint)
      .then((res) => {
        setIsLoading(false);
        if (Array.isArray(res.data.data)) {
          setTasks(res.data.data);
        } else {
          console.error("API response is not an array");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching tasks:", error);
      });
  };

  const handleFilter = () => {
    fetchTaskDetails("", "", startDate, endDate, "all");
  };

  const handleClear = () => {
    setSelectedTypes([]);
    setSelectedStatuses([...DEFAULT_SELECTED_STATUSES]);
    setSelectedCompanies([]);
    setSearchTerm("");
    setstartDate("");
    setendDate("");
    setDaysFilter("");
    fetchTaskDetails("", "", "", "", "all");
  };

  const handleChangeCompany = (selectedOptions) => {
    setSelectedCompanies(selectedOptions || []);
  };

  const filterCompanyOptions = [...new Map(
    [
      ...departments.map((dept) => dept.company_name),
      ...tasks.map((t) => t.company_name),
    ]
      .filter(Boolean)
      .map((name) => {
        const trimmed = String(name).trim();
        return [trimmed.toLowerCase(), { value: trimmed, label: trimmed }];
      })
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  const typeFilterOptions = [...new Map(
    [
      ...reminderTypes.map((t) => t.name),
      ...tasks.map((t) => t.type),
    ]
      .filter(Boolean)
      .map((name) => {
        const trimmed = String(name).trim();
        return [trimmed.toLowerCase(), { value: trimmed, label: trimmed }];
      })
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  const statusFilterOptions = [...new Map(
    [
      ...BASE_STATUS_OPTIONS,
      ...tasks.map((t) => t.status),
    ]
      .filter(Boolean)
      .map((status) => {
        const trimmed = String(status).trim();
        return [trimmed.toLowerCase(), { value: trimmed, label: trimmed }];
      })
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  const isDefaultStatusFilter =
    selectedStatuses.length === 2 &&
    selectedStatuses.some((s) => s.value === "NEW") &&
    selectedStatuses.some((s) => s.value === "RE-NEW");

  const editCompanyOptions = [...new Map(
    customers
      .filter((cust) => cust.company)
      .map((cust) => {
        const company = cust.company.trim();
        return [
          company.toLowerCase(),
          {
            value: company,
            label: `${company}${cust.phone ? ` - ${cust.phone}` : ""}`,
          },
        ];
      })
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  const getEditCompanyOptions = (currentCompany = "") => {
    const options = [...editCompanyOptions];
    const trimmed = currentCompany.trim();
    if (
      trimmed &&
      !options.some((option) => option.value.toLowerCase() === trimmed.toLowerCase())
    ) {
      options.unshift({ value: trimmed, label: trimmed });
    }
    return options;
  };

  const handleChangeStartDate = async (event) => {
    setstartDate(event.target.value);
  };

  const handleChangeEndDate = async (event) => {
    setendDate(event.target.value);
  };

  const handleChangeDaysFilter = (event) => {
    setDaysFilter(event.target.value);
  };

  const filteredTasks = tasks.filter((comp) => {
    if (selectedStatuses.length) {
      const statusValues = selectedStatuses.map((s) => s.value);
      if (!statusValues.includes(comp.status)) return false;
    }
    if (selectedTypes.length) {
      const typeValues = selectedTypes.map((t) => t.value);
      if (!typeValues.includes(comp.type)) return false;
    }
    if (selectedCompanies.length) {
      const companyValues = selectedCompanies.map((c) =>
        String(c.value).trim().toLowerCase()
      );
      const name = String(comp.company_name || "").trim().toLowerCase();
      if (!companyValues.includes(name)) return false;
    }
    if (daysFilter && !(Number(comp.days_difference) < Number(daysFilter))) {
      return false;
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      const blob = [
        comp.company_name,
        comp.type,
        comp.classification,
        comp.licence,
        comp.contact_name,
        comp.status,
        comp.remark,
      ]
        .join(" ")
        .toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const totalAmount = filteredTasks.reduce(
    (sum, comp) => sum + (Number(comp.amount) || 0),
    0
  );

  const totalAmountDays = filteredTasks.reduce(
    (sum, comp) => sum + (calcAmountDays(comp.amount, comp.duration) || 0),
    0
  );

  const activeFilterCount = [
    selectedTypes.length,
    !isDefaultStatusFilter ? selectedStatuses.length : "",
    selectedCompanies.length,
    searchTerm,
    startDate,
    endDate,
    daysFilter,
  ].filter(Boolean).length;

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    contact: "",
    classification: "",
    duration: "",
    total_system: "",
    rate: "",
    amount: "",
    start_date: "",
    end_date: "",
    licence: "",
    remark: "",
    status: "",
  });

  const openEditModal = (user) => {
    console.log("modal");
    setShowModal(true);
    setFormData({
      company_name: user.company_name || "",
      contact_name: user.contact_name || "",
      contact: user.contact || "",
      classification: user.classification || "",
      duration: user.duration || "",
      total_system: user.total_system || "",
      rate: user.rate ?? "",
      amount: user.amount ?? "",
      start_date: user.start_date || "",
      end_date: user.end_date || "",
      licence: user.licence || "",
      remark: user.remark || "",
      status: user.status || "",
    });
    setEditUser(user);
    const companyName = (user.company_name || "").trim();
    setSelectedEditCompany(
      companyName ? { value: companyName, label: companyName } : null
    );
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEditCompany(null);
  };

  const saveEditedUser = async (e) => {
    e.preventDefault();
    try {
      if (!editUser) {
        // Handle the case when editUser is null
        console.error("No user data to edit.");
        return;
      }

      const response = await http.put(
        `/reminder_edit/${editUser.id}`,
        formData
      );
      if (Array.isArray(response.data.data)) {
        toast.success("Customer data updated successfully!");
        setShowModal(false);
        fetchTaskDetails("", "", startDate, endDate, "all");
      } else {
        console.log("API response is not an array");
      }
    } catch (error) {
      console.error("Error updating user", error);
      toast.error("An error occurred while updating the customer.");
    }
  };

  const handleChangeModal = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const sendConfirm = async (userId, id) => {
    // await http.post(`/send_confirm_jc/${userId}/${id}`)
    //     .then(response => {
    //         toast.success("Confirmation Email/SMS sent");
    //         // setTasks(response.data.data);
    //         fetchTaskDetails(selectedDepartmentId, selectedStatus);
    //     })
    //     .catch(err => {
    //         console.error("Error deleting task", err);
    //     });
  };

  const openViewModal = (user) => {
    setViewData(user);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
  };

  const exportSummaryFullPDF = () => {
    // Group by type for summary section
    const grouped = {};
    filteredTasks.forEach((c) => {
      const type = c.type || "Unknown";
      if (!grouped[type]) {
        grouped[type] = { type, total_system: 0, total_rate: 0, total_amount: 0, count: 0 };
      }
      grouped[type].total_system += Number(c.total_system) || 0;
      grouped[type].total_rate += Number(c.rate) || 0;
      grouped[type].total_amount += Number(c.amount) || 0;
      grouped[type].count += 1;
    });

    const summaryRows = Object.values(grouped).sort((a, b) => a.type.localeCompare(b.type));
    const grandSystem = summaryRows.reduce((s, r) => s + r.total_system, 0);
    const grandRate = summaryRows.reduce((s, r) => s + r.total_rate, 0);
    const grandAmount = summaryRows.reduce((s, r) => s + r.total_amount, 0);

    const doc = new jsPDF({ orientation: "landscape" });

    // Title
    doc.setFontSize(16);
    doc.setTextColor(13, 110, 253);
    doc.text("Reminder Summary + Full Report", 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`, 14, 23);

    // Summary table
    doc.setFontSize(11);
    doc.setTextColor(13, 110, 253);
    doc.text("Summary by Type", 14, 31);

    autoTable(doc, {
      startY: 35,
      head: [["Type", "Count", "Total System", "Total Rate", "Total Amount"]],
      body: [
        ...summaryRows.map((r) => [
          r.type,
          r.count,
          r.total_system,
          r.total_rate.toLocaleString("en-IN"),
          r.total_amount.toLocaleString("en-IN"),
        ]),
        ["TOTAL", filteredTasks.length, grandSystem, grandRate.toLocaleString("en-IN"), grandAmount.toLocaleString("en-IN")],
      ],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 246, 255] },
      didParseCell: (data) => {
        if (data.row.index === summaryRows.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 235, 255];
          data.cell.styles.textColor = [0, 0, 0];
        }
      },
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    // Full list table
    const afterSummaryY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setTextColor(13, 110, 253);
    doc.text("Full Reminders List", 14, afterSummaryY);

    autoTable(doc, {
      startY: afterSummaryY + 5,
      head: [[
        "Created On", "Type", "Company Name", "Status",
        "Classification", "Duration", "Total System",
        "Rate", "Total Amount", "Amount(days)", "Licence", "Start", "End", "Days", "Remarks"
      ]],
      body: filteredTasks.map((c) => [
        moment(c.created_at).format("DD MMM,YY"),
        c.type || "-",
        c.company_name || "-",
        c.status || "-",
        c.classification || "-",
        c.duration || "-",
        c.total_system || "-",
        c.rate || "-",
        c.amount || "-",
        formatAmountDays(c.amount, c.duration),
        c.licence || "-",
        c.start_date ? moment(c.start_date).format("DD-MM-YYYY") : "-",
        c.end_date ? moment(c.end_date).format("DD-MM-YYYY") : "-",
        c.days_difference || "-",
        c.remark || "-",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`reminder_full_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Reminders List", 14, 15);
    doc.setFontSize(9);
    doc.text(`Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`, 14, 22);

    autoTable(doc, {
      startY: 27,
      head: [[
        "Created On", "Type", "Company Name", "Status",
        "Classification", "Duration", "Total System",
        "Rate", "Total Amount", "Amount(days)", "Licence", "Start", "End", "Days", "Remarks"
      ]],
      body: filteredTasks.map((c) => [
        moment(c.created_at).format("DD MMM,YY"),
        c.type || "-",
        c.company_name || "-",
        c.status || "-",
        c.classification || "-",
        c.duration || "-",
        c.total_system || "-",
        c.rate || "-",
        c.amount || "-",
        formatAmountDays(c.amount, c.duration),
        c.licence || "-",
        c.start_date || "-",
        c.end_date || "-",
        c.days_difference || "-",
        c.remark || "-",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`reminders_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  const exportSummaryPDF = () => {
    // Group by type
    const grouped = {};
    filteredTasks.forEach((c) => {
      const type = c.type || "Unknown";
      if (!grouped[type]) {
        grouped[type] = { type, total_system: 0, total_rate: 0, total_amount: 0, count: 0 };
      }
      grouped[type].total_system += Number(c.total_system) || 0;
      grouped[type].total_rate += Number(c.rate) || 0;
      grouped[type].total_amount += Number(c.amount) || 0;
      grouped[type].count += 1;
    });

    const summaryRows = Object.values(grouped).sort((a, b) => a.type.localeCompare(b.type));
    const grandSystem = summaryRows.reduce((s, r) => s + r.total_system, 0);
    const grandRate = summaryRows.reduce((s, r) => s + r.total_rate, 0);
    const grandAmount = summaryRows.reduce((s, r) => s + r.total_amount, 0);

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setTextColor(13, 110, 253);
    doc.text("Reminder Summary Report", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${moment().format("DD MMM YYYY, hh:mm A")}`, 14, 25);

    autoTable(doc, {
      startY: 32,
      head: [["Type", "Count", "Total System", "Total Rate", "Total Amount"]],
      body: [
        ...summaryRows.map((r) => [
          r.type,
          r.count,
          r.total_system,
          r.total_rate.toLocaleString("en-IN"),
          r.total_amount.toLocaleString("en-IN"),
        ]),
        // Grand total row
        ["TOTAL", filteredTasks.length, grandSystem, grandRate.toLocaleString("en-IN"), grandAmount.toLocaleString("en-IN")],
      ],
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 246, 255] },
      bodyStyles: { valign: "middle" },
      didParseCell: (data) => {
        // Highlight grand total row
        if (data.row.index === summaryRows.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 235, 255];
          data.cell.styles.textColor = [0, 0, 0];
        }
      },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
    });

    doc.save(`reminder_summary_${moment().format("YYYYMMDD_HHmm")}.pdf`);
  };

  const exportExcel = () => {
    const headers = [
      "Created On",
      "Type",
      "Company Name",
      "Status",
      "Classification",
      "Duration",
      "Total System",
      "Rate",
      "Total Amount",
      "Amount(days)",
      "Licence",
      "Start Date",
      "End Date",
      "Days",
      "Remarks",
    ];

    const dataRows = filteredTasks.map((c) => {
      const amountDays = calcAmountDays(c.amount, c.duration);
      return [
        moment(c.created_at).format("DD MMM,YY"),
        c.type || "",
        c.company_name || "",
        c.status || "",
        c.classification || "",
        Number(c.duration) || "",
        Number(c.total_system) || "",
        Number(c.rate) || "",
        Number(c.amount) || "",
        amountDays ?? "",
        c.licence || "",
        c.start_date ? moment(c.start_date).format("DD-MM-YYYY") : "",
        c.end_date ? moment(c.end_date).format("DD-MM-YYYY") : "",
        c.days_difference !== null && c.days_difference !== undefined && c.days_difference !== ""
          ? Number(c.days_difference)
          : "",
        c.remark || "",
      ];
    });

    const totalRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Total Amount",
      totalAmount,
      totalAmountDays,
      "",
      "",
      "",
      "",
      "",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows, totalRow]);
    applyReminderExcelStyles(ws);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reminders");
    XLSX.writeFile(wb, `reminders_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };

  return (
    <>
      <style>{`
        @keyframes blink-red {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
        .reminder-page {
          padding: 16px 8px 12px;
        }
        .reminder-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }
        .reminder-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .reminder-header h1 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 700;
          color: #1f2937;
        }
        .reminder-header-stats {
          display: flex;
          align-items: stretch;
          gap: 8px;
        }
        .reminder-stat {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 6px 14px;
          min-width: 110px;
        }
        .reminder-stat .stat-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .reminder-stat .stat-value {
          display: block;
          font-size: 16px;
          font-weight: 700;
          color: #111827;
          line-height: 1.2;
        }
        .reminder-stat.amount {
          background: #eff6ff;
          border-color: #bfdbfe;
        }
        .reminder-stat.amount .stat-value {
          color: #1d4ed8;
        }
        .reminder-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .reminder-filter-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          margin-bottom: 14px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
          overflow: visible;
        }
        .reminder-filter-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          padding: 8px 12px;
          background: #f8fafc;
        }
        .reminder-filter-body {
          padding: 12px 14px;
          border-top: 1px solid #e5e7eb;
        }
        .reminder-filter-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 10px 12px;
          align-items: start;
        }
        .reminder-filter-grid .filter-field label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .reminder-filter-grid .form-control,
        .reminder-filter-grid .form-select {
          height: 36px;
          font-size: 13px;
          padding: 4px 10px;
        }
        .reminder-filter-actions {
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
        }
        .reminder-filter-actions .btn-group-actions {
          display: flex;
          gap: 8px;
        }
        .reminder-total-pill {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1d4ed8;
          border-radius: 999px;
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
        }
        .reminder-table {
          margin-bottom: 0;
          font-size: 13px;
        }
        .reminder-table th,
        .reminder-table td {
          vertical-align: middle;
          white-space: nowrap;
          padding: 8px 10px;
        }
        .reminder-table th {
          font-size: 12px;
          font-weight: 700;
          position: sticky;
          top: 0;
          z-index: 2;
          background: #cfe2ff;
        }
        .reminder-table .amount-th {
          white-space: normal;
          line-height: 1.2;
        }
        .reminder-table .amount-th-total {
          display: block;
          margin-top: 3px;
          font-size: 11px;
          font-weight: 800;
          color: #0b5ed7;
        }
        .reminder-table .company-cell,
        .reminder-table .remark-cell,
        .reminder-table .trim-cell {
          white-space: nowrap;
          max-width: 160px;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: default;
        }
        .reminder-table .licence-cell {
          max-width: 140px;
        }
        .reminder-table tfoot td {
          background: #f8fafc;
          font-weight: 700;
        }
        @media (max-width: 1200px) {
          .reminder-filter-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 768px) {
          .reminder-filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
      `}</style>
      {showModal && (
        <form onSubmit={saveEditedUser}>
          <div className="modal" tabindex="-1" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Customer Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                  ></button>
                </div>
                <div className="modal-body" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <div className="form-group mt-3">
                    <label>Status</label>
                    <select
                      name="status"
                      className="form-control"
                      value={formData.status}
                      onChange={handleChangeModal}
                      required
                    >
                      <option value="NEW">NEW</option>
                      <option value="RE-NEW">RE-NEW</option>
                      <option value="LEFT">LEFT</option>
                      <option value="STOPPED">STOPPED</option>
                    </select>
                  </div>
                  <div className="form-group  mt-3">
                    <label>Contact Name</label>
                    <input
                      name="contact_name"
                      required
                      className="form-control"
                      type="text"
                      value={formData.contact_name}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Company</label>
                    <Select
                      options={getEditCompanyOptions(formData.company_name)}
                      value={selectedEditCompany}
                      onChange={(selectedOption) => {
                        setSelectedEditCompany(selectedOption);
                        setFormData((prev) => ({
                          ...prev,
                          company_name: selectedOption ? selectedOption.value : "",
                        }));
                      }}
                      placeholder="Search and select company..."
                      isClearable
                      isSearchable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Phone</label>
                    <input
                      name="contact"
                      required
                      className="form-control"
                      type="text"
                      value={formData.contact}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Duration</label>
                    <input
                      name="duration"
                      required
                      className="form-control"
                      type="text"
                      value={formData.duration}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Total system</label>
                    <input
                      name="total_system"
                      required
                      className="form-control"
                      type="text"
                      value={formData.total_system}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Rate</label>
                    <input
                      name="rate"
                      className="form-control"
                      type="text"
                      value={formData.rate}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Total Amount</label>
                    <input
                      name="amount"
                      className="form-control"
                      type="text"
                      value={formData.amount}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Classification</label>
                    <input
                      name="classification"
                      required
                      className="form-control"
                      type="text"
                      value={formData.classification}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Licence</label>
                    <input
                      name="licence"
                      required
                      className="form-control"
                      type="text"
                      value={formData.licence}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>Start Date</label>
                    <input
                      name="start_date"
                      required
                      className="form-control"
                      type="date"
                      value={formData.start_date}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group  mt-3">
                    <label>End Date</label>
                    <input
                      name="end_date"
                      required
                      className="form-control"
                      type="date"
                      value={formData.end_date}
                      onChange={handleChangeModal}
                    />
                  </div>
                  <div className="form-group mt-3">
                    <label>Remarks</label>
                    <textarea
                      name="remark"
                      className="form-control"
                      rows="3"
                      value={formData.remark}
                      onChange={handleChangeModal}
                      placeholder="Enter remarks"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-sm btn btn-success"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
      {viewModal && (
        <div className="modal" tabindex="-1" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{viewData.company_name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeViewModal}
                ></button>
              </div>
              <div className="modal-body">
                <p><strong>Location:</strong> {viewData.location}</p>
                <p><strong>Contact Person:</strong> {viewData.contact_name}</p>
                <p><strong>Phone:</strong> {viewData.contact}</p>
                <p><strong>Remark:</strong> {viewData.remark}</p>
                <p><strong>Distance:</strong> {viewData.distance}</p>
                <p><strong>Rate:</strong> {viewData.rate}</p>
                <p><strong>Total Amount:</strong> {viewData.amount}</p>
                <p><strong>Others:</strong> {viewData.others}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-sm btn btn-success"
                  onClick={closeViewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="reminder-page">
        <div className="reminder-header">
          <div className="reminder-header-left">
            <h1>Reminders List</h1>
            <div className="reminder-header-stats">
              <div className="reminder-stat">
                <span className="stat-label">Records</span>
                <span className="stat-value">{filteredTasks.length}</span>
              </div>
              <div className="reminder-stat amount">
                <span className="stat-label">Total Amount</span>
                <span className="stat-value">₹ {totalAmount.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>
          <div className="reminder-actions">
            <button
              className="btn btn-sm btn-primary"
              onClick={exportSummaryPDF}
              disabled={filteredTasks.length === 0}
              title="Download Summary PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} className="me-1" /> Summary
            </button>
            <button
              className="btn btn-sm btn-info"
              onClick={exportSummaryFullPDF}
              disabled={filteredTasks.length === 0}
              title="Download Summary + Full List PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} className="me-1" /> Full
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={exportPDF}
              disabled={filteredTasks.length === 0}
              title="Export as PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} className="me-1" /> PDF
            </button>
            <button
              className="btn btn-sm btn-success"
              onClick={exportExcel}
              disabled={filteredTasks.length === 0}
              title="Export as Excel"
            >
              <FontAwesomeIcon icon={faFileExcel} className="me-1" /> Excel
            </button>
            <Link className="btn btn-sm btn-outline-primary" to="/jobcart/jcvirus">
              Add Reminders
            </Link>
          </div>
        </div>

        <div className="reminder-filter-card">
          <div className="reminder-filter-bar">
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-primary ms-1">{activeFilterCount}</span>
                )}
                <FontAwesomeIcon
                  icon={showFilters ? faChevronUp : faChevronDown}
                  className="ms-2"
                />
              </button>
              {!showFilters && (
                <span className="text-muted" style={{ fontSize: "12px" }}>
                  Click Filters to search / refine list
                </span>
              )}
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: "220px" }}
                placeholder="Search company, type, licence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {showFilters && (
            <div className="reminder-filter-body">
              <div className="reminder-filter-grid">
                <div className="filter-field">
                  <label>Type</label>
                  <Select
                    options={typeFilterOptions}
                    value={selectedTypes}
                    onChange={(opts) => setSelectedTypes(opts || [])}
                    placeholder="Search type..."
                    isMulti
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="filter-field">
                  <label>Status</label>
                  <Select
                    options={statusFilterOptions}
                    value={selectedStatuses}
                    onChange={(opts) => setSelectedStatuses(opts || [])}
                    placeholder="Search status..."
                    isMulti
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="filter-field">
                  <label>Company</label>
                  <Select
                    options={filterCompanyOptions}
                    value={selectedCompanies}
                    onChange={handleChangeCompany}
                    placeholder="Search company..."
                    isMulti
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="filter-field">
                  <label>From</label>
                  <input
                    name="start_date"
                    value={startDate}
                    onChange={handleChangeStartDate}
                    className="form-control"
                    type="date"
                  />
                </div>

                <div className="filter-field">
                  <label>To</label>
                  <input
                    name="end_date"
                    value={endDate}
                    onChange={handleChangeEndDate}
                    className="form-control"
                    type="date"
                  />
                </div>

                <div className="filter-field">
                  <label>Days</label>
                  <select
                    className="form-control"
                    value={daysFilter}
                    onChange={handleChangeDaysFilter}
                  >
                    <option value="">All Days</option>
                    <option value="30">Less than 30 Days</option>
                    <option value="60">Less than 60 Days</option>
                    <option value="90">Less than 90 Days</option>
                  </select>
                </div>
              </div>

              <div className="reminder-filter-actions">
                <div className="btn-group-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary px-3"
                    onClick={() => {
                      handleFilter();
                      setShowFilters(false);
                    }}
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary px-3"
                    onClick={() => {
                      handleClear();
                      setShowFilters(false);
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      <Toaster />

      <div className="table-responsive" style={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "auto" }}>
        {isLoading ? (
          <TaskSkeleton />
        ) : (
            <table className="table table-hover reminder-table mb-0">
              <thead className="table-primary">
                <tr>
                  <th>Created</th>
                  <th>Type</th>
                  <th>Company Name</th>
                  <th>Status</th>
                  <th>Classification</th>
                  <th>Duration</th>
                  <th>Systems</th>
                  <th>Rate</th>
                  <th className="amount-th">
                    Amount
                    <span className="amount-th-total">
                      ₹ {totalAmount.toLocaleString("en-IN")}
                    </span>
                  </th>
                  <th className="amount-th">
                    Amount(days)
                    <span className="amount-th-total">
                      {totalAmountDays.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  </th>
                  <th>Licence</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Days</th>
                  <th>Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="16" className="text-center text-muted py-4">
                      No reminders found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((comp) => {
                  const days = Number(comp.days_difference);
                  const isOverdue = !isNaN(days) && days < 0;
                  const isExpiringSoon = !isNaN(days) && days >= 0 && days < 30;
                  return (
                  <tr key={comp.id} style={
                    isOverdue
                      ? { backgroundColor: "#fff1f1" }
                      : isExpiringSoon
                      ? { backgroundColor: "#fff7ed" }
                      : {}
                  }>
                    <td>{moment(comp.created_at).format("DD-MM-YY")}</td>
                    <td>
                      <b>{comp.type}</b>
                    </td>
                    <td
                      className="company-cell font-weight-bold"
                      title={comp.company_name || ""}
                    >
                      {comp.company_name}
                    </td>
                    <td>
                      <span className={statusBadgeClass(comp.status)}>
                        {comp.status}
                      </span>
                    </td>
                    <td
                      className="trim-cell"
                      title={comp.classification || ""}
                    >
                      {comp.classification || "-"}
                    </td>
                    <td>{comp.duration}</td>
                    <td>{comp.total_system}</td>
                    <td>{comp.rate != null && comp.rate !== "" ? Number(comp.rate).toLocaleString("en-IN") : "-"}</td>
                    <td>{comp.amount != null && comp.amount !== "" ? Number(comp.amount).toLocaleString("en-IN") : "-"}</td>
                    <td>{formatAmountDays(comp.amount, comp.duration)}</td>
                    <td
                      className="trim-cell licence-cell"
                      title={comp.licence || ""}
                    >
                      {comp.licence || "-"}
                    </td>
                    <td>{comp.start_date ? moment(comp.start_date).format("DD-MM-YY") : "-"}</td>
                    <td>{comp.end_date ? moment(comp.end_date).format("DD-MM-YY") : "-"}</td>
                    <td>
                      {isOverdue || isExpiringSoon ? (
                        <span style={{
                          backgroundColor: isOverdue ? "#8b0000" : "#dc3545",
                          color: "#fff",
                          fontWeight: "bold",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          display: "inline-block",
                          animation: isOverdue ? "blink-red 0.7s step-start infinite" : "blink-red 1s step-start infinite",
                          fontSize: "12px"
                        }}>
                          {comp.days_difference}
                        </span>
                      ) : (
                        <b>{comp.days_difference}</b>
                      )}
                    </td>
                    <td
                      className="remark-cell"
                      title={comp.remark || ""}
                    >
                      {comp.remark || "-"}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          className="btn btn-outline-primary"
                          style={{ padding: "2px 6px", fontSize: "11px" }}
                          onClick={() => openViewModal(comp)}
                          title="View"
                        >
                          <FontAwesomeIcon icon={faFile} />
                        </button>
                        {allowEdit && user.department_id == 1 && (
                          <button
                            className="btn btn-outline-warning"
                            style={{ padding: "2px 6px", fontSize: "11px" }}
                            onClick={() => openEditModal(comp)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        )}
                        <button
                          className="btn btn-outline-secondary"
                          style={{ padding: "2px 6px", fontSize: "11px" }}
                          onClick={() => {
                            if (window.confirm("Are you sure you want send the emails?")) {
                              sendConfirm(user.customer_id, user.id);
                            }
                          }}
                          title="Send Email"
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
                )}
              </tbody>
              {filteredTasks.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="8" className="text-end">Total Amount</td>
                    <td>₹ {totalAmount.toLocaleString("en-IN")}</td>
                    <td>{totalAmountDays.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                    <td colSpan="6"></td>
                  </tr>
                </tfoot>
              )}
            </table>
        )}
      </div>
      </div>
    </>
  );
}
